import { Link } from "react-router";
import { api } from "../../data";
import { PillView, useAction } from "../../components/ui";
import { date, time } from "../../lib/format";
import { Fehler, Laden } from "./parts";
import { pille } from "./konsole-model";
import { OwnerSelect, RabattVorschlag, SchnellErfassen, StufePill, StufeWahl, useKundenakte, useLead, useStaff, Verlauf } from "./crm-teile";
import { heuteBerlin, leadForOrg, setOwner, type KundenakteDaten } from "./crm-data";

/**
 * Kundenakte (Vertrieb-Konsole, Inhaber 02.11.2026): Erweiterung der Agentur-Detailseite um den Vertrieb. Zuständig, Stufe,
 * Thema, nächster Schritt und Verlauf aus dem Lead des Kontos, Kontakt des Inhabers, Herkunft (Registrierung) und Signale
 * (Testende, letzte Anmeldung, Aktivierung, offene Tickets, Zahlungsproblem, Kündigungswunsch). Für Admin und Vertrieb gleich;
 * die Admin-Blöcke der Detailseite stehen darunter und fehlen für den Vertrieb.
 */
export function KundenakteVertrieb({ orgId }: { orgId: string }) {
  const q = useKundenakte(orgId);
  if (q.error) return <Fehler error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending || !q.data) return <Laden />;
  const d = q.data;
  return (
    <div className="grid tk-crm-kundenakte">
      {d.lead_id ? <LeadBlock leadId={d.lead_id} /> : <OhneLead orgId={orgId} />}
      <div className="stack">
        <KontaktBlock d={d} />
        <SignaleBlock d={d} />
      </div>
    </div>
  );
}

function OhneLead({ orgId }: { orgId: string }) {
  const { busy, error, run } = useAction();
  return (
    <section className="panel stack" aria-labelledby="ka-vertrieb">
      <h2 id="ka-vertrieb">Vertrieb</h2>
      <p>Dieses Konto wird noch nicht im Vertrieb geführt. Mit dem Lead gibt es einen Zuständigen, Stufe, nächsten Schritt und Verlauf; Ereignisse des Kontos (Aktivierung, Zahlung, Kündigungswunsch) landen dann von selbst dort.</p>
      <div className="row">
        <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "fuehren"} onClick={() => void run("fuehren", () => leadForOrg(api, orgId), "Konto wird jetzt im Vertrieb geführt.")}>Im Vertrieb führen</button>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
    </section>
  );
}

function LeadBlock({ leadId }: { leadId: string }) {
  const q = useLead(leadId);
  const staff = useStaff();
  const { busy, run } = useAction();
  const l = q.data?.lead;
  return (
    <section className="panel stack" aria-labelledby="ka-vertrieb">
      <div className="row between"><h2 id="ka-vertrieb">Vertrieb</h2>{l && <Link to={`/intern/leads/${l.id}`}>Lead-Akte öffnen</Link>}</div>
      {q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : !l ? <Laden /> : (
        <>
          <div className="row tk-crm-kopfzeile"><StufePill stufe={l.stage} />{l.next_step_at && <small>Nächster Schritt: {l.next_step ?? "–"} · {date(l.next_step_at)}</small>}</div>
          <div className="grid tk-crm-aufgabe">
            <StufeWahl leadId={l.id} stufe={l.stage} disabled={!!l.objected_at} />
            <OwnerSelect value={l.owner_id} disabled={!!busy || !!l.objected_at} onChange={(owner) => void run("owner", () => setOwner(api, l.id, owner), "Zuständig geändert.")} />
          </div>
          {l.topic && <p><strong>Thema:</strong> {l.topic}</p>}
          {!l.objected_at && <SchnellErfassen leadId={l.id} ownerId={l.owner_id} />}
          <h3 className="tk-crm-unter">Verlauf</h3>
          <Verlauf eintraege={q.data?.verlauf ?? []} staff={staff.data} max={6} />
          {(q.data?.verlauf.length ?? 0) > 6 && <Link to={`/intern/leads/${l.id}`}>Ganzen Verlauf in der Lead-Akte</Link>}
          {!l.objected_at && <div className="row"><RabattVorschlag leadId={l.id} /></div>}
        </>
      )}
    </section>
  );
}

function KontaktBlock({ d }: { d: KundenakteDaten }) {
  const k = d.kontakt;
  return (
    <section className="panel stack" aria-labelledby="ka-kontakt">
      <h2 id="ka-kontakt">Kontakt des Inhabers</h2>
      {!k || k.admins.length === 0 ? <p className="muted">Kein aktiver Admin.</p> : (
        <ul className="tk-betrieb-liste">
          {k.admins.map((a) => (
            <li key={a.email}>{a.name ? <strong>{a.name}</strong> : null}{a.owner ? <small> · Inhaber</small> : null}<br /><a href={`mailto:${a.email}`}>{a.email}</a></li>
          ))}
        </ul>
      )}
      <dl className="tk-crm-fakten">
        {k?.legal_name && <div><dt>Firma</dt><dd>{k.legal_name}</dd></div>}
        {k?.country && <div><dt>Land</dt><dd>{k.country}</dd></div>}
        {k?.phone && <div><dt>Telefon</dt><dd><a href={`tel:${k.phone.replace(/[^\d+]/g, "")}`}>{k.phone}</a></dd></div>}
        {k?.website && <div><dt>Website</dt><dd>{k.website}</dd></div>}
        <div><dt>Herkunft</dt><dd>{d.herkunft ?? "nicht bekannt"}</dd></div>
      </dl>
    </section>
  );
}

/** Tage bis zum Testende (negativ: abgelaufen), nach dem Kalendertag in deutscher Zeit */
export function tageBis(iso: string | null, heute: string): number | null {
  if (!iso) return null;
  const ende = heuteBerlin(new Date(iso));
  return Math.round((Date.parse(`${ende}T00:00:00Z`) - Date.parse(`${heute}T00:00:00Z`)) / 86_400_000);
}

function SignaleBlock({ d }: { d: KundenakteDaten }) {
  const s = d.signale;
  if (!s) return null;
  const tage = s.status === "trial" ? tageBis(s.trial_ends_at, heuteBerlin()) : null;
  const a = s.aktivierung;
  return (
    <section className="panel stack" aria-labelledby="ka-signale">
      <h2 id="ka-signale">Signale</h2>
      <ul className="tk-crm-signale">
        {tage !== null && <li><PillView pill={pille(tage < 0 ? `Test seit ${-tage} ${-tage === 1 ? "Tag" : "Tagen"} abgelaufen` : tage === 0 ? "Test endet heute" : `Test endet in ${tage} ${tage === 1 ? "Tag" : "Tagen"}`, tage <= 3 ? "warning" : "info")} />
          {s.trial_ends_at && <small> {date(s.trial_ends_at)}{s.zahlungsart ? " · zahlt danach automatisch" : " · ohne Zahlungsart"}</small>}</li>}
        <li><strong>Letzte Anmeldung:</strong> {s.letzte_anmeldung ? `${date(s.letzte_anmeldung)} ${time(s.letzte_anmeldung)}` : "noch keine erfasst"}</li>
        <li><strong>Aktivierung:</strong> {a.activated ? <PillView pill={pille("aktiv", "success")} /> : <PillView pill={pille("noch nicht", "neutral")} />}
          <small> {a.creators} Creator · {a.deals} Kooperationen · {a.vertraege_versendet} Verträge versendet · {a.rechnungen} Rechnungen · Instagram {a.instagram ? "verbunden" : "nicht verbunden"}</small></li>
        <li><strong>Offene Tickets:</strong> {s.tickets_offen}</li>
        {s.zahlungsproblem && <li><PillView pill={pille("Zahlungsproblem", "error")} /> <small>Abbuchung gescheitert oder Zahlung offen</small></li>}
        {s.kuendigung && <li><PillView pill={pille("Kündigungswunsch", "warning")} /> <small>am {date(s.kuendigung.am)}{s.kuendigung.wirksam ? `, wirksam ${date(s.kuendigung.wirksam)}` : ""}{s.kuendigung.grund ? ` · Grund: ${s.kuendigung.grund}` : ""}</small></li>}
      </ul>
    </section>
  );
}
